import {
  allDays,
  defaultAveragePower,
  defaultPeakPower,
  defaultWorkingDays,
  defaultWorkingHoursEnd,
  defaultWorkingHoursStart,
  nighttimePowerRatio,
  timeIntervalMinutes,
} from '../constants';
import { PowerProfileDataPoint } from '../types';

/**
 * Parses time string (HH:MM) to hour and minute numbers
 * @param timeString - Time in HH:MM format
 * @returns Object with hour and minute numbers
 */
const parseTime = (timeString: string): { hour: number; minute: number } => {
  const [hourStr, minuteStr] = timeString.split(':');
  return {
    hour: parseInt(hourStr),
    minute: parseInt(minuteStr),
  };
};

/**
 * Calculates power value for a given time slot
 * @param hour - Current hour (0-23)
 * @param minute - Current minute (0-59)
 * @param isWorkingDay - Whether it's a working day
 * @param startHour - Working hours start hour
 * @param startMinute - Working hours start minute
 * @param endHour - Working hours end hour
 * @param endMinute - Working hours end minute
 * @param peakPowerKw - Peak power value
 * @param averagePowerKw - Average power value
 * @returns Power value in kW
 */
const calculatePowerValue = (
  hour: number,
  minute: number,
  isWorkingDay: boolean,
  startHour: number,
  startMinute: number,
  endHour: number,
  endMinute: number,
  peakPowerKw: number,
  averagePowerKw: number
): number => {
  if (isWorkingDay) {
    // Convert times to minutes for easier calculation
    const currentTimeMinutes = hour * 60 + minute;
    const startTimeMinutes = startHour * 60 + startMinute;
    const endTimeMinutes = endHour * 60 + endMinute;

    // Check if current time is within working hours
    if (currentTimeMinutes >= startTimeMinutes && currentTimeMinutes < endTimeMinutes) {
      // Calculate the exact middle of working hours
      const workingDurationMinutes = endTimeMinutes - startTimeMinutes;
      const middleTimeMinutes = startTimeMinutes + workingDurationMinutes / 2;

      // Peak power for 1 hour (60 minutes) centered on the middle of working hours
      const peakStartMinutes = middleTimeMinutes - 30; // 30 minutes before middle
      const peakEndMinutes = middleTimeMinutes + 30; // 30 minutes after middle

      if (currentTimeMinutes >= peakStartMinutes && currentTimeMinutes < peakEndMinutes) {
        return peakPowerKw;
      } else {
        // Average power for the rest of working hours
        return averagePowerKw;
      }
    } else {
      // Nighttime on working days: 20% of average power
      return averagePowerKw * nighttimePowerRatio;
    }
  } else {
    // Weekends: 20% of average power
    return averagePowerKw * nighttimePowerRatio;
  }
};

/**
 * Generates power profile data for one week based on configuration
 * @param peakPowerKw - Peak power in kW
 * @param averagePowerKw - Average power in kW
 * @param workingDays - Array of working days
 * @param workingHoursStart - Working hours start time (HH:MM format)
 * @param workingHoursEnd - Working hours end time (HH:MM format)
 * @returns Array of power profile data points
 */
export const generatePowerProfileData = (
  peakPowerKw: number = defaultPeakPower,
  averagePowerKw: number = defaultAveragePower,
  workingDays: string[] = defaultWorkingDays,
  workingHoursStart: string = defaultWorkingHoursStart,
  workingHoursEnd: string = defaultWorkingHoursEnd
): PowerProfileDataPoint[] => {
  // Parse working hours
  const startTime = parseTime(workingHoursStart);
  const endTime = parseTime(workingHoursEnd);

  const generatedData: PowerProfileDataPoint[] = [];

  // Get Monday of current week
  const now = new Date();
  const dayOfWeek = now.getDay();
  const mondayOffset = dayOfWeek === 0 ? -6 : 1 - dayOfWeek; // Monday is 1, Sunday is 0
  const monday = new Date(now);
  monday.setDate(now.getDate() + mondayOffset);
  monday.setHours(0, 0, 0, 0);

  // Generate data for one week (7 days)
  for (let dayIndex = 0; dayIndex < 7; dayIndex++) {
    const dayName = allDays[dayIndex];
    const isWorkingDay = workingDays.includes(dayName);
    const currentDay = new Date(monday);
    currentDay.setDate(monday.getDate() + dayIndex);

    // Generate data every 15 minutes throughout the day
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += timeIntervalMinutes) {
        const date = new Date(currentDay);
        date.setHours(hour, minute, 0, 0);

        const powerValue = calculatePowerValue(
          hour,
          minute,
          isWorkingDay,
          startTime.hour,
          startTime.minute,
          endTime.hour,
          endTime.minute,
          peakPowerKw,
          averagePowerKw
        );

        generatedData.push({
          timestamp: date.toISOString(),
          power: Math.round(powerValue * 10) / 10, // Round to 1 decimal
          day: dayName,
          hour: hour,
          dayIndex: dayIndex,
        });
      }
    }
  }

  return generatedData;
};

/**
 * Formats tooltip data for PowerProfileChart
 * @param value - Power value in kW
 * @param _name - Unused parameter (required by Recharts)
 * @param props - Tooltip props from Recharts
 * @returns Array of formatted strings for tooltip display
 */
export const formatPowerProfileTooltip = (
  value: number,
  _name: string,
  props: { payload?: { timestamp: string; day: string; hour: number } }
): { [key: string]: string } => {
  const payload = props.payload;
  if (!payload || !payload.timestamp || !payload.day) {
    return {};
  }

  const timestamp = payload.timestamp;
  const day = payload.day;

  if (timestamp.includes(':') && !timestamp.includes('T')) {
    const parts = timestamp.split(':');
    const timeStr = `${parts[1]}:${parts[2]}`;

    return {
      Power: `${value.toFixed(1)} kW`,
      Time: `${day}, ${timeStr}`,
    };
  }

  // Fallback to original logic for ISO timestamps
  const date = new Date(timestamp);
  const timeStr = date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'UTC',
  });

  return {
    Power: `${value.toFixed(1)} kW`,
    Time: `${day}, ${timeStr}`,
  };
};
